<template>
    <div class="tw-px-4">
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-6">
          <div class="tw-flex tw-items-center tw-gap-6">
            <div>
              <h1 class="tw-text-3xl tw-font-bold tw-text-gray-900">
                {{ $t('pages.companies', 'Companies') }}
              </h1>
              <p class="tw-text-gray-600 tw-mt-1">
                {{ $t('pages.companies-description', 'Connect with industry experts and thought leaders') }}
              </p>
            </div>

          </div>
        </div>

        <div class="tw-grid tw-grid-cols-12 tw-gap-4">
            <!-- Left Sidebar - Filters -->
            <div class="tw-col-span-12 lg:tw-col-span-3">
                <div class="tw-bg-white tw-border tw-border-primary-200 tw-rounded-lg tw-p-4 tw-sticky tw-top-4 tw-min-h-[calc(100vh-200px)]">
                    <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-4">
                        {{ $t('common.filters', 'Filters') }}
                    </h3>
                    
                    <!-- Search Filter -->
                    <div class="tw-mb-4">
                        <FormField 
                            v-model="search" 
                            type="text" 
                            :hide-label="true" 
                            :hide-errors="true" 
                            :placeholder="$t('common.search-companies', 'Search companies...')"
                            left-icon="solar:magnifer-linear" 
                            @input="onSearch" 
                            class="tw-bg-gray-50 tw-rounded-md" 
                        />
                    </div>

                    <!-- Category Filter -->
                    <div class="tw-mb-4">
                        <FormMultiSelect
                            v-model="selectedCategory"
                            :options="categoryOptions"
                            :placeholder="$t('common.filter-by-category', 'Filter by category')"
                            left-icon="solar:widget-bold-duotone"
                            class="tw-bg-white tw-rounded-md"
                            :hide-errors="true"
                            option-value="id"
                            @change="onCategoryChange"
                        />
                    </div>

                    <!-- Country Filter -->
                    <div class="tw-mb-4">
                        <FormMultiSelect
                            v-model="selectedCountry"
                            :options="countryOptions"
                            :placeholder="$t('common.filter-by-country', 'Filter by country')"
                            left-icon="solar:flag-bold-duotone"
                            class="tw-bg-white tw-rounded-md"
                            :hide-errors="true"
                            option-value="id"
                            @change="onCategoryChange"
                        />
                    </div>

                    <!-- Certificates Filter -->
                    <div class="tw-mb-4">
                        <FormMultiSelect
                            v-model="selectedCertificate"
                            :options="certificateOptions"
                            :placeholder="$t('common.filter-by-certificate', 'Filter by certificate')"
                            left-icon="solar:medal-ribbons-star-bold-duotone"
                            class="tw-bg-white tw-rounded-md"
                            :hide-errors="true"
                            option-value="id"
                            @change="onCategoryChange"
                        />
                    </div>

                    <!-- Additional filters can be added here in the future -->
                    <div v-if="false" class="tw-text-sm tw-text-gray-500 tw-text-center tw-py-4">
                        {{ $t('common.more-filters-coming', 'More filters coming soon...') }}
                    </div>

                    <!-- Clear Filters button -->
                    <div class="tw-mb-4 tw-flex tw-justify-center">
                        <a-button @click="resetFilters" intent="link" left-icon="solar:refresh-bold-duotone">
                            {{ $t('buttons.clear-filters', 'Clear Filters') }}
                        </a-button>
                    </div>
                </div>
            </div>

            <!-- Right Side - Companies Grid -->
            <div class="tw-col-span-12 lg:tw-col-span-9">
                <!-- Header -->
                <div v-if="false" class="tw-flex tw-items-center tw-justify-between tw-mb-6">
                    <div>
                        <h2 class="tw-text-2xl tw-font-bold tw-text-gray-900">
                            {{ $t('common.companies', 'Companies') }}
                        </h2>
                        <p v-if="companies.length > 0" class="tw-text-sm tw-text-gray-600 tw-mt-1">
                            {{ $t('common.showing-companies', 'Showing {count} companies', { count: companies.length }) }}
                        </p>
                    </div>
                </div>

                <!-- Loading State -->
                <div v-if="loading && companies.length === 0" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                    <div v-for="i in 12" :key="i" class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-xl tw-shadow-smx tw-relative tw-p-5 tw-flex tw-flex-col tw-items-center tw-justify-between tw-min-h-[260px] tw-h-full">
                        <!-- Bookmark skeleton -->
                        <div class="tw-absolute tw-top-3 tw-right-3 tw-w-6 tw-h-6 tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
                        
                        <!-- Company info skeleton -->
                        <div class="tw-w-full tw-flex tw-flex-col tw-items-start tw-mb-4">
                            <!-- Company name skeleton -->
                            <div class="tw-h-5 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-3/4 tw-mb-1"></div>
                            <!-- Pavilion skeleton -->
                            <div class="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-1/2"></div>
                        </div>

                        <!-- Logo skeleton -->
                        <div class="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-w-full tw-mb-2">
                            <div class="tw-w-full tw-h-28 tw-bg-gray-200 tw-rounded-md tw-animate-pulse"></div>
                        </div>
                    </div>
                </div>

                <!-- Companies Grid -->
                <div v-else-if="companies.length > 0" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
                    <CompanyListCard 
                        v-for="company in companies" 
                        :key="company.id" 
                        :company="company" 
                    />
                </div>

                <!-- Empty State -->
                <div v-else class="tw-text-center tw-py-12 tw-min-h-[calc(100vh-300px)] tw-flex tw-items-center tw-justify-center">
                    <div class="tw-max-w-md tw-mx-auto">
                        <img :src="$frontend('assets/images/empty-state/companies.svg')" class="tw-w-full tw-max-w-[200px] tw-h-full tw-object-cover tw-rounded-lg tw-mx-auto tw-mb-4" />
                        <h3 class="tw-text-lg tw-font-medium tw-text-gray-900 tw-mb-2">
                            {{ $t('messages.no-companies-found', 'No companies found') }}
                        </h3>
                        <p class="tw-text-gray-500 tw-mb-6">
                            {{ $t('messages.no-companies-description', 'Try adjusting your search or filters') }}
                        </p>
                        <a-button 
                            @click="resetFilters" 
                            intent="primary"
                            left-icon="solar:refresh-bold-duotone"
                        >
                            {{ $t('buttons.reset-filters', 'Reset Filters') }}
                        </a-button>
                    </div>
                </div>

                <!-- Load More Button -->
                <div v-if="hasMorePages && companies.length > 0" class="tw-text-center tw-mt-8">
                    <a-button 
                        @click="loadMore" 
                        :loading="loadingMore"
                        intent="gradient"
                        class="tw-min-w-[200px]"
                    >
                        {{ $t('buttons.load-more', 'Load More Companies') }}
                    </a-button>
                </div>

                <!-- End of Results -->
                <div v-if="!hasMorePages && companies.length > 0" class="tw-text-center tw-mt-8 tw-py-4">
                    <p class="tw-text-sm tw-text-gray-500">
                        {{ $t('messages.end-of-results', 'You\'ve reached the end of the results') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField.vue';
import FormMultiSelect from '@/components/form/FormMultiSelect.vue';
import CompanyListCard from '@/components/companies/CompanyListCard.vue';
import companyApi from '@/api/companies';
import appApi from '@/api/app';
import useAlert from '@/composables/useAlert';

const { t } = useI18n();

// Reactive data
const loading = ref(false);
const loadingMore = ref(false);
const companies = ref([]);
const categories = ref([]);
const search = ref('');
const selectedCategory = ref([]);
const countries = ref([]);
const certificates = ref([]);
const selectedCountry = ref([]);
const selectedCertificate = ref([]);

// Pagination
const currentPage = ref(1);
const hasMorePages = ref(true);
const perPage = 12; // 4 items per row * 3 rows initially

// Search debounce
let searchTimeout = null;

// Computed properties
const categoryOptions = computed(() => {
    const options = [
        // { id: '', name: t('common.all-categories', 'All Categories') }
    ];
    
    if (categories.value.length > 0) {
        options.push(...categories.value.map(cat => ({
            id: cat.id,
            name: cat.name
        })));
    }
    
    return options;
});

const countryOptions = computed(() => {
    return countries.value.map(c => ({ id: c.id, name: c.name }));
});

const certificateOptions = computed(() => {
    return certificates.value.map(c => ({ id: c.id, name: c.name }));
});

// Methods
const fetchCategories = async () => {
    try {
        const response = await appApi.forms.categories('company');
        categories.value = response.data || [];
    } catch (error) {
        console.error('Error fetching categories:', error);
        useAlert(t('errors.failed-to-load-categories', 'Failed to load categories'), 'danger');
    }
};

const fetchCountries = async () => {
    try {
        const response = await appApi.forms.countries();
        countries.value = response.data || [];
    } catch (error) {
        console.error('Error fetching countries:', error);
        useAlert(t('errors.failed-to-load-countries', 'Failed to load countries'), 'danger');
    }
};

const fetchCertificates = async () => {
    try {
        const response = await appApi.forms.certificates('company');
        certificates.value = response.data || [];
    } catch (error) {
        console.error('Error fetching certificates:', error);
        useAlert(t('errors.failed-to-load-certificates', 'Failed to load certificates'), 'danger');
    }
};

const fetchAllFilters = async () => {
    await Promise.all([
        fetchCategories(),
        fetchCountries(),
        fetchCertificates()
    ]);
};

const getSelectedIds = (arr) => arr.map(item => typeof item === 'object' ? item.id : item);

const fetchCompanies = async (page = 1, append = false) => {
    if (page === 1) {
        loading.value = true;
    } else {
        loadingMore.value = true;
    }

    try {
        const params = {
            page,
            per_page: perPage,
            search: search.value.trim(),
            category_id: selectedCategory.value.length ? getSelectedIds(selectedCategory.value) : [],
            country_id: selectedCountry.value.length ? getSelectedIds(selectedCountry.value) : [],
            certificate_id: selectedCertificate.value.length ? getSelectedIds(selectedCertificate.value) : [],
        };

        const response = await companyApi.paginate(params);

        if (response.data && response.data.data) {
            if (append) {
                companies.value.push(...response.data.data);
            } else {
                companies.value = response.data.data;
            }

            // Check if there are more pages
            hasMorePages.value = response.data.current_page < response.data.last_page;
            currentPage.value = response.data.current_page;
        }
    } catch (error) {
        console.error('Error fetching companies:', error);
        useAlert(t('errors.failed-to-load-companies', 'Failed to load companies'), 'danger');
    } finally {
        loading.value = false;
        loadingMore.value = false;
    }
};

const onSearch = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        hasMorePages.value = true;
        fetchCompanies(1);
    }, 500);
};

const onCategoryChange = () => {
    currentPage.value = 1;
    hasMorePages.value = true;
    fetchCompanies(1);
};

const loadMore = () => {
    if (!loadingMore.value && hasMorePages.value) {
        fetchCompanies(currentPage.value + 1, true);
    }
};

const resetFilters = () => {
    search.value = '';
    selectedCategory.value = [];
    selectedCountry.value = [];
    selectedCertificate.value = [];
    currentPage.value = 1;
    hasMorePages.value = true;
    fetchCompanies(1);
};

// Initialize
onMounted(async () => {
    await Promise.all([
        fetchAllFilters(),
        fetchCompanies()
    ]);
});

// Remove onCategoryChange and direct calls in watchers
const triggerFilterChange = () => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        currentPage.value = 1;
        hasMorePages.value = true;
        fetchCompanies(1);
    }, 500);
};

// Watchers
watch(search, () => {
    triggerFilterChange();
});
watch(selectedCategory, () => {
    triggerFilterChange();
});
watch(selectedCountry, () => {
    triggerFilterChange();
});
watch(selectedCertificate, () => {
    triggerFilterChange();
});
</script>

<style scoped>
/* Add any custom styles here */
</style>